import type { DrawingDocument, Point2D, TextAnnotation } from '../model';
import type { EditorSelection } from './editor';

// #756 / #638 -- the internal clipboard for standalone text. Only a text-only selection is
// copyable here; selections mixing text with structure or other annotations stay guarded until
// #638's faithful mixed clipboard exists (docs/model/drawing-ui-selection-editing.md).

// The selected text annotations when the selection is text and nothing else; otherwise null.
export function textOnlySelection(document: DrawingDocument, selection: EditorSelection): TextAnnotation[] | null {
  const ids = selection.annotationIds ?? [];
  if (selection.atomIds.length || selection.bondIds.length || ids.length === 0) return null;
  const texts = document.annotations.filter(
    (annotation): annotation is TextAnnotation => annotation.kind === 'text' && ids.includes(annotation.id)
  );
  return texts.length === ids.length ? texts : null;
}

// Pastes copies with fresh ids, keeping their layout: the block nearest the top-left lands at
// `target` (depiction coordinates).
export function pasteTexts(
  document: DrawingDocument,
  texts: readonly TextAnnotation[],
  target: Point2D,
  newId: () => string = () => globalThis.crypto?.randomUUID?.() ?? `text-${Math.random().toString(36).slice(2)}`
): { readonly document: DrawingDocument; readonly pastedIds: readonly string[] } {
  if (texts.length === 0) return { document, pastedIds: [] };
  const anchorX = Math.min(...texts.map((text) => text.position.x));
  const anchorY = Math.min(...texts.map((text) => text.position.y));
  // Text-only copies do not include their targets; detach rather than bind to unrelated IDs.
  const pasted = texts.map((text) => {
    const { attachment: _attachment, ...copy } = structuredClone(text);
    return {
      ...copy,
      id: newId(),
      position: { x: text.position.x - anchorX + target.x, y: text.position.y - anchorY + target.y }
    };
  });
  return {
    document: { ...document, annotations: [...document.annotations, ...pasted] },
    pastedIds: pasted.map((text) => text.id)
  };
}
